import { ref, computed, watch } from 'vue'
import { useTenantBrandingStore } from '@/stores/tenantBranding'
import { useAuthStore } from '@/stores/auth'
import { applyTheme, resetTheme } from '@/utils/themeManager'

/**
 * Composable for managing tenant branding
 * Handles loading, applying, and updating tenant branding configuration
 */
export function useBranding() {
    const brandingStore = useTenantBrandingStore()
    const authStore = useAuthStore()

    // State
    const isLoading = ref(false)
    const error = ref(null)

    // Computed
    const branding = computed(() => brandingStore.branding)
    const isCustomBranded = computed(() => brandingStore.isCustomBranded)
    const primaryColor = computed(() => brandingStore.primaryColor)
    const secondaryColor = computed(() => brandingStore.secondaryColor)
    const logo = computed(() => brandingStore.logo)
    const businessName = computed(() => brandingStore.businessName)
    const favicon = computed(() => brandingStore.favicon)
    const customDomain = computed(() => brandingStore.customDomain)
    const themePreset = computed(() => brandingStore.themePreset)

    /**
     * Load tenant branding from API
     * @param {string} tenantId - Optional tenant ID (uses current tenant if not provided)
     */
    const loadBranding = async (tenantId = null) => {
        isLoading.value = true
        error.value = null

        try {
            const targetTenantId = tenantId || authStore.user?.tenantId

            if (!targetTenantId) {
                console.warn('No tenant ID available for branding')
                resetTheme()
                return
            }

            await brandingStore.fetchBranding(targetTenantId)
            applyBranding()
        } catch (err) {
            error.value = err.message
            console.error('Failed to load branding:', err)
            // Apply default theme on error
            resetTheme()
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Apply current branding to the page
     */
    const applyBranding = () => {
        if (!brandingStore.branding) {
            resetTheme()
            return
        }

        // Apply theme colors and styles
        applyTheme(brandingStore.branding)

        // Update page title
        if (brandingStore.businessName) {
            document.title = `${brandingStore.businessName} - Gestionale`
        }

        // Update favicon
        if (brandingStore.favicon) {
            updateFavicon(brandingStore.favicon)
        }

        // Update meta tags
        updateMetaTags()
    }

    /**
     * Update favicon
     * @param {string} faviconUrl - URL to favicon image
     */
    const updateFavicon = (faviconUrl) => {
        let link = document.querySelector("link[rel*='icon']")
        if (!link) {
            link = document.createElement('link')
            link.rel = 'icon'
            document.head.appendChild(link)
        }
        link.href = faviconUrl
    }

    /**
     * Update meta tags for branding
     */
    const updateMetaTags = () => {
        // Update theme-color meta tag
        let metaTheme = document.querySelector('meta[name="theme-color"]')
        if (!metaTheme) {
            metaTheme = document.createElement('meta')
            metaTheme.name = 'theme-color'
            document.head.appendChild(metaTheme)
        }
        metaTheme.content = brandingStore.primaryColor || '#8B4513'

        // Update apple-mobile-web-app-title
        let metaAppleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]')
        if (!metaAppleTitle) {
            metaAppleTitle = document.createElement('meta')
            metaAppleTitle.name = 'apple-mobile-web-app-title'
            document.head.appendChild(metaAppleTitle)
        }
        metaAppleTitle.content = brandingStore.businessName || 'Pasticceria'
    }

    /**
     * Update branding configuration
     * @param {Object} config - Branding configuration object
     */
    const updateBranding = async (config) => {
        isLoading.value = true
        error.value = null

        try {
            await brandingStore.updateBranding(config)
            applyBranding()
            return { success: true }
        } catch (err) {
            error.value = err.message
            console.error('Failed to update branding:', err)
            return { success: false, error: err.message }
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Upload logo file
     * @param {File} file - Logo image file
     */
    const uploadLogo = async (file) => {
        isLoading.value = true
        error.value = null

        try {
            const result = await brandingStore.uploadLogo(file)
            applyBranding()
            return result
        } catch (err) {
            error.value = err.message
            console.error('Failed to upload logo:', err)
            throw err
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Upload favicon file
     * @param {File} file - Favicon image file
     */
    const uploadFavicon = async (file) => {
        isLoading.value = true
        error.value = null

        try {
            const result = await brandingStore.uploadFavicon(file)
            applyBranding()
            return result
        } catch (err) {
            error.value = err.message
            console.error('Failed to upload favicon:', err)
            throw err
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Reset to default branding
     */
    const resetBranding = async () => {
        isLoading.value = true
        error.value = null

        try {
            await brandingStore.resetBranding()
            resetTheme()
            return { success: true }
        } catch (err) {
            error.value = err.message
            console.error('Failed to reset branding:', err)
            return { success: false, error: err.message }
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Load custom fonts
     * @param {Array} fonts - Array of font configurations
     */
    const loadCustomFonts = (fonts) => {
        if (!fonts || !Array.isArray(fonts)) return

        fonts.forEach(font => {
            if (font.url && font.family) {
                const fontFace = new FontFace(font.family, `url(${font.url})`)
                fontFace.load().then(loadedFont => {
                    document.fonts.add(loadedFont)
                }).catch(err => {
                    console.error(`Failed to load font ${font.family}:`, err)
                })
            } else if (font.googleFont) {
                // Load from Google Fonts
                const link = document.createElement('link')
                link.href = `https://fonts.googleapis.com/css2?family=${font.googleFont.replace(' ', '+')}:wght@300;400;500;600;700&display=swap`
                link.rel = 'stylesheet'
                document.head.appendChild(link)
            }
        })
    }

    /**
     * Apply theme preset
     * @param {string} preset - Preset name (classic, modern, elegant)
     */
    const applyPreset = async (preset) => {
        isLoading.value = true
        error.value = null

        try {
            await brandingStore.applyPreset(preset)
            applyBranding()
            return { success: true }
        } catch (err) {
            error.value = err.message
            console.error('Failed to apply preset:', err)
            return { success: false, error: err.message }
        } finally {
            isLoading.value = false
        }
    }

    /**
     * Validate custom domain
     * @param {string} domain - Domain to validate
     */
    const validateCustomDomain = async (domain) => {
        try {
            return await brandingStore.validateDomain(domain)
        } catch (err) {
            error.value = err.message
            console.error('Domain validation failed:', err)
            return { valid: false, error: err.message }
        }
    }

    /**
     * Get CSS variable value
     * @param {string} varName - CSS variable name (without --)
     */
    const getCSSVariable = (varName) => {
        return getComputedStyle(document.documentElement)
            .getPropertyValue(`--${varName}`)
            .trim()
    }

    /**
     * Get tenant from custom domain
     * @param {string} domain - Custom domain
     */
    const getTenantByDomain = async (domain) => {
        try {
            return await brandingStore.getTenantByDomain(domain)
        } catch (err) {
            error.value = err.message
            console.error('Failed to get tenant by domain:', err)
            return null
        }
    }

    // Watch for auth changes to reload branding
    watch(() => authStore.user?.tenantId, (newTenantId) => {
        if (newTenantId) {
            loadBranding(newTenantId)
        } else {
            resetTheme()
        }
    })

    return {
        // State
        isLoading,
        error,

        // Computed
        branding,
        isCustomBranded,
        primaryColor,
        secondaryColor,
        logo,
        businessName,
        favicon,
        customDomain,
        themePreset,

        // Methods
        loadBranding,
        applyBranding,
        updateBranding,
        uploadLogo,
        uploadFavicon,
        resetBranding,
        loadCustomFonts,
        applyPreset,
        validateCustomDomain,
        getCSSVariable,
        getTenantByDomain
    }
}
